import * as partialInvoiceApi from "@/apiv2/partialInvoice";
import * as pdfApi from "@/apiv2/pdf";
import * as companySessionSelectors from "@/components/new/CompanySession/company-session.selectors";
import { createAction, createSetterActions } from "@/redux/helpers/action";
import { handleError } from "@/redux/utils/error";
import { STATUS } from "@/utils/constants";

import { AMOUNT_MODE_LINE_ITEMS, MODULE } from "./constants";

export const [
  setIsOpen,
  setInvoiceDate,
  setActiveTab,
  setDisplayMode,
  setLastGeneratedDisplayMode,
  setOneLinerDescription,
  setGroupedLabourLabel,
  setGroupedMaterialsLabel,
  setInvoiceDisplayModeStatus,
  setAmountMode,
  setSelectedItemUids,
  setItemAmounts,
  setDeliveryFee,
  setSpecificAmount,
  setPercentage,
  setPartialSummary,
  setPartialSummaryStatus,
  setAdditionalProducts,
  setAdditionalProductsStatus,
  setLastSubmittedId,
  setLastSubmittedPayload,
] = createSetterActions(MODULE, [
  "isOpen",
  "invoiceDate",
  "activeTab",
  "displayMode",
  "lastGeneratedDisplayMode",
  "oneLinerDescription",
  "groupedLabourLabel",
  "groupedMaterialsLabel",
  "invoiceDisplayModeStatus",
  "amountMode",
  "selectedItemUids",
  "itemAmounts",
  "deliveryFee",
  "specificAmount",
  "percentage",
  "partialSummary",
  "partialSummaryStatus",
  "additionalProducts",
  "additionalProductsStatus",
  "lastSubmittedId",
  "lastSubmittedPayload",
]);

export const reset = createAction(`${MODULE}/RESET`);

export const savePartialInvoicePdfUrl =
  ({ pdfUrl, partialInvoice }) =>
  async (dispatch) => {
    if (!partialInvoice || !pdfUrl) {
      return;
    }
    try {
      await partialInvoiceApi.postPartialInvoicePdfUrl({
        data: { pdf_url: pdfUrl, partial_invoice: partialInvoice },
      })();
    } catch (error) {
      handleError(error, dispatch);
    }
  };

export const fetchPartialDisplayMode =
  ({ orderUid }) =>
  async (dispatch, getState) => {
    if (!orderUid) {
      return;
    }

    const state = getState();
    dispatch(setInvoiceDisplayModeStatus(STATUS.PENDING));
    try {
      const response = await pdfApi.getInvoiceDisplayMode({
        orderUid,
        invoiceType: "partial",
      })();
      const data = response?.data;
      const invoiceDisplayMode = data?.invoiceDisplayMode;
      if (invoiceDisplayMode) {
        dispatch(setDisplayMode(invoiceDisplayMode));
        if (data?.isPreviouslyGenerated) {
          dispatch(setLastGeneratedDisplayMode(invoiceDisplayMode));
        }
      }
      // When the order has no grouped labels yet (e.g. full_breakdown orders
      // that were never generated in grouped mode), the API returns null for
      // these. Fall back to the company invoicing settings so switching to
      // grouped mode pre-fills the company's configured labels.
      const invoiceSettings = companySessionSelectors.getInvoiceSettings(state);
      const labourLabel = data?.groupedLabourLabel ?? invoiceSettings?.groupedLabourLabel;
      if (labourLabel != null) {
        dispatch(setGroupedLabourLabel(labourLabel));
      }
      const materialsLabel = data?.groupedMaterialsLabel ?? invoiceSettings?.groupedMaterialsLabel;
      if (materialsLabel != null) {
        dispatch(setGroupedMaterialsLabel(materialsLabel));
      }
      if (data?.oneLinerDescription != null) {
        dispatch(setOneLinerDescription(data.oneLinerDescription));
      }
      dispatch(setInvoiceDisplayModeStatus(STATUS.FULFILLED));
    } catch (error) {
      dispatch(setInvoiceDisplayModeStatus(STATUS.REJECTED));
      handleError(error, dispatch);
    }
  };

export const fetchPartialSummary =
  ({ customerUid, orderUid }) =>
  async (dispatch) => {
    if (!customerUid || !orderUid) {
      return;
    }
    dispatch(setPartialSummaryStatus(STATUS.PENDING));
    try {
      const { data } = await partialInvoiceApi.getPartialInvoices({ customerUid, orderUid })();
      dispatch(setPartialSummary(data ?? null));
      dispatch(setPartialSummaryStatus(STATUS.FULFILLED));
    } catch (error) {
      dispatch(setPartialSummaryStatus(STATUS.REJECTED));
      handleError(error, dispatch);
    }
  };

export const fetchAdditionalProducts =
  ({ customerUid, orderUid }) =>
  async (dispatch) => {
    if (!customerUid || !orderUid) {
      return;
    }
    dispatch(setAdditionalProductsStatus(STATUS.PENDING));
    try {
      const { data } = await partialInvoiceApi.getPartialInvoiceAdditionalProducts({
        customerUid,
        orderUid,
      })();
      // The endpoint returns { data: [...items], meta: {...} }. Store the wrapper
      // so the panel can read both the rows and the metadata.
      dispatch(setAdditionalProducts(data ?? { data: [], meta: {} }));
      dispatch(setAdditionalProductsStatus(STATUS.FULFILLED));
    } catch (error) {
      dispatch(setAdditionalProductsStatus(STATUS.REJECTED));
      handleError(error, dispatch);
    }
  };

export { AMOUNT_MODE_LINE_ITEMS };
